'use client';
import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useSyncExternalStore,
  useEffect,
  type ReactNode,
} from 'react';
import { subscribe, notifyListeners } from '@/components/shared/SaveJobButton/SaveJobButton';
import { GreenhouseCustomFieldId, type GreenhouseJob } from '@/libs/greenhouse/greenhouse';
import {
  getJobLocations,
  getJobTeams,
  getJobsByGreenhouseField,
  getSavedJobIds,
  pruneSavedJobIds,
} from '@/libs/greenhouse/greenhouse-functions';

interface GreenhouseContextValue {
  jobs: GreenhouseJob[];
  getJob: (id: number) => GreenhouseJob | undefined;
  featuredJobs: GreenhouseJob[];
  remoteJobs: GreenhouseJob[];
  jobLocations: string[];
  jobTeams: string[];
  savedJobIds: number[];
  savedJobs: GreenhouseJob[];
}

const GreenhouseContext = createContext<GreenhouseContextValue>({
  jobs: [],
  getJob: () => undefined,
  featuredJobs: [],
  remoteJobs: [],
  jobLocations: [],
  jobTeams: [],
  savedJobIds: [],
  savedJobs: [],
});

const EMPTY_SAVED_IDS: number[] = [];

export const GreenhouseProvider = ({ children, jobs }: { children: ReactNode; jobs: GreenhouseJob[] }) => {
  const getJob = useCallback((id: number) => jobs.find(job => job.id === id), [jobs]);
  const featuredJobs = useMemo(() => getJobsByGreenhouseField(jobs, GreenhouseCustomFieldId.FeaturedJob), [jobs]);
  const remoteJobs = useMemo(() => getJobsByGreenhouseField(jobs, GreenhouseCustomFieldId.OpenToRemote), [jobs]);
  const jobLocations = useMemo(() => getJobLocations(jobs), [jobs]);
  const jobTeams = useMemo(() => getJobTeams(jobs), [jobs]);
  const savedJobIds = useSyncExternalStore(subscribe, getSavedJobIds, () => EMPTY_SAVED_IDS);

  useEffect(() => {
    if (jobs.length === 0) return;
    const validIds = jobs.map(job => job.id);
    if (pruneSavedJobIds(validIds)) {
      notifyListeners();
    }
  }, [jobs]);

  const savedJobs = useMemo(() => jobs.filter(job => savedJobIds.includes(job.id)), [jobs, savedJobIds]);

  const value = useMemo(
    () => ({ jobs, getJob, featuredJobs, remoteJobs, jobLocations, jobTeams, savedJobIds, savedJobs }),
    [jobs, getJob, featuredJobs, remoteJobs, jobLocations, jobTeams, savedJobIds, savedJobs],
  );
  return <GreenhouseContext.Provider value={value}>{children}</GreenhouseContext.Provider>;
};

export const useGreenhouse = () => useContext(GreenhouseContext);
